import type { AdminRole, AdminUser } from '@diana/contracts';
import { useState, type FormEvent } from 'react';

import {
  Select,
  CollectionHeader,
  CollectionPrimary,
  CollectionRow,
  CollectionSurface,
} from '../../ui/index.js';
import { EditorHeading } from '../settings/SettingsEditor.js';
import './UsersEditor.css';

type UsersEditorProps = {
  users: AdminUser[];
  embedded?: boolean;
  invite(input: { email: string; role: AdminRole }): Promise<AdminUser>;
  changeRole(id: string, role: AdminRole): Promise<AdminUser>;
  changeActive(id: string, active: false): Promise<AdminUser>;
};

export function UsersEditor({
  users: initial,
  embedded = false,
  invite,
  changeRole,
  changeActive,
}: UsersEditorProps) {
  const [users, setUsers] = useState(initial);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AdminRole>('Editor');
  const [message, setMessage] = useState('');

  const replace = (user: AdminUser) =>
    setUsers((current) => current.map((item) => (item.id === user.id ? user : item)));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setMessage('');
    try {
      const invited = await invite({ email, role });
      setUsers((current) => [...current, invited]);
      setEmail('');
      setMessage('Invitation created');
    } catch {
      setMessage('Could not invite this user');
    }
  };
  const Wrapper = embedded ? 'section' : 'article';

  return (
    <Wrapper
      className={`users-editor${embedded ? ' settings-panel users-editor--embedded' : ' editor'}`}
    >
      <EditorHeading title="Users" level={embedded ? 2 : 1} />
      <form className="form-section invite-form" onSubmit={(event) => void submit(event)}>
        <div className="form-section__heading">
          <p className="eyebrow">Invite</p>
          <h2>Add a collaborator</h2>
        </div>
        <label className="field">
          <span>Email address</span>
          <input
            aria-label="Email address"
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
          />
        </label>
        <label className="field">
          <span>Role</span>
          <Select
            aria-label="New user role"
            value={role}
            onChange={(event) => setRole(event.currentTarget.value as AdminRole)}
          >
            <option>Editor</option>
            <option>Owner</option>
          </Select>
        </label>
        <button className="button button--primary" type="submit">
          Invite user
        </button>
        {message ? <p role="status">{message}</p> : null}
      </form>

      <section className="form-section">
        <div className="form-section__heading">
          <p className="eyebrow">Access</p>
          <h2>Current users</h2>
        </div>
        <CollectionSurface className="user-list">
          <CollectionHeader className="user-list__head" aria-hidden="true">
            <span>User</span>
            <span>Role</span>
            <span>Access</span>
          </CollectionHeader>
          {users.map((user) => (
            <CollectionRow className="user-row" key={user.id}>
              <CollectionPrimary title={user.email} detail={user.active ? 'Active' : 'Inactive'} />
              <label className="field field--compact">
                <span className="sr-only">{user.email} role</span>
                <Select
                  aria-label={`${user.email} role`}
                  value={user.role}
                  disabled={!user.active}
                  onChange={(event) =>
                    void changeRole(user.id, event.currentTarget.value as AdminRole).then(replace)
                  }
                >
                  <option>Editor</option>
                  <option>Owner</option>
                </Select>
              </label>
              <button
                className="button button--quiet"
                type="button"
                disabled={!user.active}
                onClick={() => void changeActive(user.id, false).then(replace)}
                aria-label={`Deactivate ${user.email}`}
              >
                Deactivate
              </button>
            </CollectionRow>
          ))}
        </CollectionSurface>
      </section>
    </Wrapper>
  );
}
